import { useEffect, useState } from "react";
import { Modal } from "../ui/modal";
import { client } from "../../sdkAdapter";
import Swal from 'sweetalert2';
import Badge from "../ui/badge/Badge";

interface OrderDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: number | null;
}

export default function OrderDetailsModal({
    isOpen,
    onClose,
    orderId,
}: OrderDetailsModalProps) {
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [statuses, setStatuses] = useState<any[]>([]);
    const [selectedStatusId, setSelectedStatusId] = useState<number | string>("");
    const [updating, setUpdating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<any>({});
    const [paymentModes, setPaymentModes] = useState<any[]>([]);
    const [shippingServices, setShippingServices] = useState<any[]>([]);

    useEffect(() => {
        if (isOpen && orderId) {
            fetchOrderDetails(orderId);
            fetchStatuses();
            fetchDependencies();
        } else {
            setOrder(null);
            setSelectedStatusId("");
            setIsEditing(false);
        }
    }, [isOpen, orderId]);

    const fetchDependencies = async () => {
        try {
            const [pmRes, ssRes] = await Promise.all([
                (client as any).getApiSettingsPaymentModes(),
                (client as any).getApiShippingShippingServices()
            ]);
            setPaymentModes((pmRes as any).data?.data || (pmRes as any).data || []);
            setShippingServices((ssRes as any).data?.data || (ssRes as any).data || []);
        } catch (err) {
            console.error("Failed to fetch dependencies:", err);
        }
    };

    const fetchStatuses = async () => {
        try {
            const res = await (client as any).getApiOrdersStatus() as any;
            const data = res.data?.data || res.data || [];
            setStatuses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch statuses:", err);
        }
    };

    const fetchOrderDetails = async (id: number) => {
        setLoading(true);
        setError("");
        try {
            const res = await client.getApiOrdersOrderId({ id: String(id) }) as any;
            let data = res.data?.data || res.data;
            if (Array.isArray(data)) {
                data = data[0] || null;
            }
            setOrder(data);
            if (data) {
                const statusId = data.order_status_id || data.status_id;
                if (statusId) {
                    setSelectedStatusId(statusId);
                }
            }
        } catch (err) {
            console.error("Failed to fetch order details:", err);
            setError("Failed to load order details.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEdit = () => {
        if (!isEditing && order) {
            setEditData({
                email: order.email || order.users?.email || "",
                mobile_number: order.mobile_number || order.users?.mobile_number || "",
                comments: order.comments || "",
                status_id: order.order_status_id || order.status_id || "",
                payment_mode_id: order.payment_mode_id || order.payment_mode?.id || order.paymentMode?.id || "",
                shipping_mode_id: order.shipping_mode_id || order.shipping_services?.id || order.shippingServices?.id || "",
                discount_amount: order.discount_amount || 0,
                billing: {
                    id: order.billing_details?.id || order.billingDetails?.id,
                    address_1: order.billing_details?.address_1 || order.billingDetails?.address_1 || "",
                    city: order.billing_details?.city || order.billingDetails?.city || "Dubai",
                },
                shipping: {
                    id: order.shipping_details?.id || order.shippingDetails?.id,
                    address_1: order.shipping_details?.address_1 || order.shippingDetails?.address_1 || "",
                    city: order.shipping_details?.city || order.shippingDetails?.city || "Dubai",
                },
                product_id: order.products?.map((p: any) => p.id) || [],
                product_quantity: order.products?.map((p: any) => p.pivot?.product_quantity || p.quantity || p.qty || 1) || []
            });
        }
        setIsEditing(!isEditing);
    };

    const handleSave = async () => {
        if (!orderId) return;
        setUpdating(true);
        try {
            const payload: any = {
                id: orderId,
                status_id: Number(editData.status_id),
                email: String(editData.email || ""),
                mobile_number: String(editData.mobile_number || ""),
                comments: String(editData.comments || ""),
                payment_mode_id: Number(editData.payment_mode_id),
                shipping_mode_id: Number(editData.shipping_mode_id),
                discount_amount: Number(editData.discount_amount),
                product_id: editData.product_id,
                product_quantity: editData.product_quantity.map(Number)
            };

            if (editData.billing) {
                payload.billing = {
                    ...order.billing_details,
                    ...editData.billing,
                    uae_id: order.billing_details?.uae_id || '123456789',
                    save: 0
                };
            }
            if (editData.shipping) {
                payload.shipping = {
                    ...order.shipping_details,
                    ...editData.shipping,
                    uae_id: order.shipping_details?.uae_id || '123456789',
                    save: 0
                };
            }

            await (client as any).postApiOrdersOrderUpdateId({
                id: String(orderId),
                postApiOrdersOrderUpdateIdRequest: payload
            });

            setIsEditing(false);
            fetchOrderDetails(orderId);
            Swal.fire({
                title: 'Success',
                text: "Order updated successfully!",
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err: any) {
            console.error("Failed to update order:", err);
            Swal.fire({
                title: 'Error',
                text: "Failed to update order: " + (err?.response?.data?.message || err?.message || "Unknown error"),
                icon: 'error'
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!orderId || !selectedStatusId) return;
        setUpdating(true);
        try {
            await (client as any).postApiOrdersOrderUpdateId({
                id: String(orderId),
                postApiOrdersOrderUpdateIdRequest: {
                    id: orderId,
                    status_id: Number(selectedStatusId),
                }
            });
            fetchOrderDetails(orderId);
            Swal.fire({
                title: 'Success',
                text: "Order status updated successfully!",
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err: any) {
            console.error("Failed to update status:", err);
            Swal.fire({
                title: 'Error',
                text: "Failed to update status: " + (err?.message || "Unknown error"),
                icon: 'error'
            });
        } finally {
            setUpdating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-[800px] p-0 overflow-hidden">
            <div className="max-h-[90vh] overflow-y-auto p-6 custom-scrollbar">
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-100 pb-4 dark:border-gray-800">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Order Details #{orderId}
                        </h3>
                        <div className="flex items-center gap-3">
                            {!isEditing ? (
                                <button
                                    onClick={handleToggleEdit}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit Order
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={updating}
                                        className="px-3 py-1.5 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {updating ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}
                            {order && !isEditing && (
                                <Badge
                                    size="sm"
                                    color={
                                        (() => {
                                            const statusName = statuses.find(s => s.id === (order.order_status_id || order.status_id))?.name || order.status || order.statuses?.[0]?.name || 'Pending';
                                            return statusName === "Delivered" || statusName === "Complete" || statusName === "Shipped" ? "success" : statusName === "Pending" ? "warning" : statusName === "Cancelled" ? "error" : "primary";
                                        })()
                                    }
                                >
                                    {statuses.find(s => s.id === (order.order_status_id || order.status_id))?.name || order.status || order.statuses?.[0]?.name || 'Pending'}
                                </Badge>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="text-gray-500">Loading order details...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-500 p-4">{error}</div>
                    ) : order ? (
                        <div className="space-y-6">
                            {/* Status update quick action when NOT editing */}
                            {!isEditing && statuses.length > 0 && (
                                <div className="flex items-center justify-between p-3 bg-brand-50/50 dark:bg-brand-900/10 rounded-lg border border-brand-100 dark:border-brand-900/20">
                                    <span className="text-sm font-medium text-brand-700 dark:text-brand-400 text-nowrap mr-4">Quick Status Update:</span>
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={selectedStatusId}
                                            onChange={(e) => setSelectedStatusId(e.target.value)}
                                            disabled={updating}
                                            className="text-sm border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white py-1 px-2 focus:ring-1 focus:ring-brand-500 outline-none"
                                        >
                                            <option value="">Select Status</option>
                                            {statuses.map(status => (
                                                <option key={status.id} value={status.id}>{status.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={handleUpdateStatus}
                                            disabled={updating || !selectedStatusId || selectedStatusId == (order.order_status_id || order.status_id)}
                                            className="text-white bg-brand-500 hover:bg-brand-600 px-3 py-1 rounded text-sm disabled:opacity-50 transition-colors"
                                        >
                                            {updating ? '...' : 'Update'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Customer Section */}
                                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                                    <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Customer Details</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-xs text-gray-500">Name</label>
                                            <p className="font-medium text-gray-800 dark:text-white">
                                                {order.users ? `${order.users.first_name || ''} ${order.users.last_name || ''}`.trim() : 'Guest'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500">Email</label>
                                            {isEditing ? (
                                                <input
                                                    type="email"
                                                    value={editData.email}
                                                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                                    className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                />
                                            ) : (
                                                <p className="text-sm text-gray-700 dark:text-gray-300">{order.email || order.users?.email || 'N/A'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500">Phone</label>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={editData.mobile_number}
                                                    onChange={(e) => setEditData({ ...editData, mobile_number: e.target.value })}
                                                    className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                />
                                            ) : (
                                                <p className="text-sm text-gray-700 dark:text-gray-300">{order.mobile_number || order.users?.mobile_number || 'N/A'}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Order Info Section */}
                                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                                    <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Order Configuration</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-xs text-gray-500">Payment Mode</label>
                                            {isEditing ? (
                                                <select
                                                    value={editData.payment_mode_id}
                                                    onChange={(e) => setEditData({ ...editData, payment_mode_id: e.target.value })}
                                                    className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                >
                                                    <option value="">Select Payment</option>
                                                    {paymentModes.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                                                </select>
                                            ) : (
                                                <p className="text-sm text-gray-700 dark:text-gray-300">{order.payment_mode?.name || order.paymentMode?.name || 'N/A'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500">Shipping Service</label>
                                            {isEditing ? (
                                                <select
                                                    value={editData.shipping_mode_id}
                                                    onChange={(e) => setEditData({ ...editData, shipping_mode_id: e.target.value })}
                                                    className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                >
                                                    <option value="">Select Shipping</option>
                                                    {shippingServices.map(ss => <option key={ss.id} value={ss.id}>{ss.name}</option>)}
                                                </select>
                                            ) : (
                                                <p className="text-sm text-gray-700 dark:text-gray-300">{order.shipping_services?.name || order.shippingServices?.name || 'N/A'}</p>
                                            )}
                                        </div>
                                        {isEditing && (
                                            <>
                                                <div>
                                                    <label className="text-xs text-gray-500">Order Status</label>
                                                    <select
                                                        value={editData.status_id}
                                                        onChange={(e) => setEditData({ ...editData, status_id: e.target.value })}
                                                        className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    >
                                                        <option value="">Select Status</option>
                                                        {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500">Discount Percentage (%)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={editData.discount_amount}
                                                        onChange={(e) => setEditData({ ...editData, discount_amount: Number(e.target.value) })}
                                                        className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Address Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                                    <h4 className="text-sm font-semibold mb-3">Billing Address</h4>
                                    {isEditing ? (
                                        <textarea
                                            value={editData.billing.address_1}
                                            onChange={(e) => setEditData({ ...editData, billing: { ...editData.billing, address_1: e.target.value } })}
                                            rows={3}
                                            className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                                            {order.billing_details?.address || order.billingDetails?.address || order.billing_details?.address_1 || 'N/A'}
                                        </p>
                                    )}
                                </div>
                                <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                                    <h4 className="text-sm font-semibold mb-3">Shipping Address</h4>
                                    {isEditing ? (
                                        <textarea
                                            value={editData.shipping.address_1}
                                            onChange={(e) => setEditData({ ...editData, shipping: { ...editData.shipping, address_1: e.target.value } })}
                                            rows={3}
                                            className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                                            {order.shipping_details?.address || order.shippingDetails?.address || order.shipping_details?.address_1 || 'N/A'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Invoices Section */}
                            {((order.invoices && order.invoices.length > 0) || order.invoice) && (
                                <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">Invoices</h4>
                                    <div className="space-y-2">
                                        {/* New Invoices Relationship */}
                                        {order.invoices && order.invoices.length > 0 ? (
                                            order.invoices.map((invoice: any) => (
                                                <div key={invoice.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{invoice.code}</span>
                                                        <span className="text-xs text-gray-500">{new Date(invoice.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <Badge size="sm" color={invoice.status === 'paid' ? 'success' : 'warning'}>
                                                            {invoice.status}
                                                        </Badge>
                                                        <a
                                                            href={invoice.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors"
                                                            title="Download Invoice"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                                            </svg>
                                                        </a>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            /* Legacy Invoice Fallback */
                                            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Invoice (Legacy)</span>
                                                    <span className="text-xs text-gray-500">Generated at Order Creation</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge size="sm" color="primary">
                                                        Archived
                                                    </Badge>
                                                    <a
                                                        href={order.invoice}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors"
                                                        title="Download Invoice"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                                        </svg>
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Items Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 flex items-center justify-between">
                                    Ordered Items
                                    {isEditing && <span className="text-xs font-normal text-gray-500">(Adjust quantities as needed)</span>}
                                </h4>
                                <div className="border rounded-xl overflow-hidden border-gray-200 dark:border-gray-800 shadow-sm">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 font-medium">
                                            <tr>
                                                <th className="px-4 py-3">Product</th>
                                                <th className="px-4 py-3 text-right" style={{ width: '100px' }}>Qty</th>
                                                <th className="px-4 py-3 text-right">Price</th>
                                                <th className="px-4 py-3 text-right">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {order.products?.map((item: any, idx: number) => {
                                                const price = Number(item.price || 0);
                                                const qty = isEditing
                                                    ? editData.product_quantity[idx]
                                                    : (item.pivot?.product_quantity || item.quantity || item.qty || 0);

                                                return (
                                                    <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                                                        <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium">
                                                            {item.title || item.name || item.product_name}
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {isEditing ? (
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={qty}
                                                                    onChange={(e) => {
                                                                        const newQtys = [...editData.product_quantity];
                                                                        newQtys[idx] = Number(e.target.value);
                                                                        setEditData({ ...editData, product_quantity: newQtys });
                                                                    }}
                                                                    className="w-16 text-right text-sm px-2 py-1 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-600 dark:text-gray-400">{qty}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                                                            ${price.toFixed(2)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">
                                                            ${(price * qty).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Comments Section */}
                            <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                                <h4 className="text-sm font-semibold mb-2">Order Comments</h4>
                                {isEditing ? (
                                    <textarea
                                        value={editData.comments}
                                        onChange={(e) => setEditData({ ...editData, comments: e.target.value })}
                                        rows={2}
                                        placeholder="Add notes about this order..."
                                        className="w-full text-sm px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    />
                                ) : (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                                        {order.comments || 'No comments left for this order.'}
                                    </p>
                                )}
                            </div>

                            {/* Totals */}
                            <div className="flex flex-col items-end pt-4 border-t border-gray-100 dark:border-gray-800">
                                <div className="w-full sm:w-1/2 md:w-1/3 space-y-3">
                                    <div className="flex justify-between text-sm text-gray-500">
                                        <span>Subtotal</span>
                                        <span>${(() => {
                                            const subtotal = order.products?.reduce((acc: number, item: any, idx: number) => {
                                                const qty = isEditing ? editData.product_quantity[idx] : (item.pivot?.product_quantity || item.quantity || item.qty || 0);
                                                return acc + (Number(item.price || 0) * qty);
                                            }, 0) || 0;
                                            return subtotal.toFixed(2);
                                        })()}</span>
                                    </div>
                                    {Number(order.discount_amount || editData.discount_amount || 0) > 0 && (
                                        <div className="flex justify-between text-sm text-gray-500">
                                            <span>Discount ({order.discount_amount || editData.discount_amount}%)</span>
                                            <span>-${(() => {
                                                const subtotal = order.products?.reduce((acc: number, item: any, idx: number) => {
                                                    const qty = isEditing ? editData.product_quantity[idx] : (item.pivot?.product_quantity || item.quantity || item.qty || 0);
                                                    return acc + (Number(item.price || 0) * qty);
                                                }, 0) || 0;
                                                const discount = Number(isEditing ? editData.discount_amount : order.discount_amount);
                                                return ((subtotal * discount) / 100).toFixed(2);
                                            })()}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-100 dark:border-gray-800">
                                        <span>Total Amount</span>
                                        <span>${(() => {
                                            const subtotal = order.products?.reduce((acc: number, item: any, idx: number) => {
                                                const qty = isEditing ? editData.product_quantity[idx] : (item.pivot?.product_quantity || item.quantity || item.qty || 0);
                                                return acc + (Number(item.price || 0) * qty);
                                            }, 0) || 0;
                                            const discount = Number(isEditing ? editData.discount_amount : order.discount_amount || 0);
                                            const total = subtotal - (subtotal * discount) / 100;
                                            return total.toFixed(2);
                                        })()}</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="text-center py-10 text-gray-500">Order not found.</div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
